import * as React from 'react';
import {
    Progress,
    Tooltip,
    message,
    Collapse,
    Modal,
} from 'antd';
import {
    CheckCircleFilled,
    CloseOutlined,
    ExclamationCircleFilled,
    CloseCircleFilled,
    CloudDownloadOutlined,
} from '@ant-design/icons';
import { StylesProps, CssType } from '../../theme/jss-types';
import { useTranslation } from 'react-i18next';
import lodash from 'lodash';
import GenericHoc from './generic-hoc';
import { HocOptions } from './generic-hoc.types';
import { createAndDownloadCSVFileFromJSON } from '../../utils/csv-utils';

// Using a module-level variable to persist across component instances
let isUploadInProgress = false;

const checkUploadInProgress = () => {
    return isUploadInProgress;
};

const setUploadStatus = (status: any) => {
    isUploadInProgress = status;
};

const styles = (): CssType => ({
    floatingContainer: {
        position: 'fixed',
        zIndex: 1000,
        bottom: '20px',
        left: '20px',
        backgroundColor: '#FFFFFF',
        width: '400px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'auto',
        transition: 'all 0.3s ease',
    },
    collapseWrapper: {
        borderRadius: '12px',
        overflow: 'hidden',
        '& .ant-collapse': {
            border: 'none',
            backgroundColor: 'transparent',
        },
        '& .ant-collapse-item': {
            border: 'none',
        },
        '& .ant-collapse-header': {
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .ant-collapse-content': {
            border: 'none',
        },
        '& .ant-collapse-content-box': {
            padding: '0 24px 24px',
        },
    },
    collapsible: {
        '& .ant-collapse-arrow': {
            fontSize: '16px !important',
            marginRight: '20px !important',
            right: '30px !important',
        },
    },
    panel: {
        borderRadius: '12px',
        overflow: 'hidden',
        '& .anticon-right': {
            position: 'relative',
            right: '60px',
        },
        '& .ant-collapse-header': {

            '& .ant-collapse-extra': {
                position: 'relative',
                top: '10px',
                right: '-16px',
            },
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '24px',
    },
    progressData: {
        backgroundColor: '#d2dcde',
        fontSize: '14px',
        color: '#2f2f2f',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
    },
    progressMetrics: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    metricTile: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '33%',
        padding: '16px',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '-3px 8px 5px 1px rgb(0 0 0 / 5%)',
    },
    metricNumber: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#111111',
        marginBottom: '8px',
    },
    metricStatus: {
        fontSize: '14px',
        color: '#666666',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    closeIcon: {
        color: '#666666',
        cursor: 'pointer',
        fontSize: '16px',
        '&:hover': {
            color: '#333333',
        },
    },
});

interface AsyncBatchModalProps extends StylesProps<ReturnType<typeof styles>> {
    isVisible: boolean;
    heading: string;
    apiCallFn: (requestData: any) => Promise<any>;
    data: any[];
    getRequestBody: (batchData: any[]) => any;
    onClose: () => void;
    failureArrayKeysToShow: string[];
    isSingleApi?: boolean;
    onAllBatchesComplete?: () => Promise<any>;
    dataHeaders?: any;
    concurrencyLimit?: number;
}

const AsyncBatchModal: React.FC<AsyncBatchModalProps> = (props) => {
    const { t } = useTranslation();
    const {
        classes,
        isVisible,
        heading,
        data,
        getRequestBody,
        apiCallFn,
        onClose,
        failureArrayKeysToShow,
        dataHeaders = [],
        concurrencyLimit = 3,
        isSingleApi = false,
        onAllBatchesComplete,
    } = props;

    const [pendingCount, setPendingCount] = React.useState(data.length);
    const [successCount, setSuccessCount] = React.useState(0);
    const [failures, setFailures] = React.useState<any[]>([]);
    const [failureCount, setFailureCount] = React.useState(0);
    const [isProcessing, setIsProcessing] = React.useState(false);

    const progressPercentage = pendingCount === 0 ? 100 : Math.floor(((successCount + failureCount) / data.length) * 100);
    const jobComplete = pendingCount === 0;

    const processBatches = async () => {
        if (isProcessing) return;
        setIsProcessing(true);

        const batchSize = isSingleApi ? 1 : 25;
        const batchedData = lodash.chunk(data, batchSize);
        const promisesFns = batchedData.map((batch, index) => {
            return async () => {
                const dataForRequestBody = isSingleApi ? batch[0] : [...dataHeaders, ...batch];
                const requestBody = getRequestBody(dataForRequestBody);
                try {
                    const response = await apiCallFn({
                        ...requestBody,
                        // for elastic search
                        bulkUploadData: {
                            batchNumber: index + 1,
                            totalBatches: batchedData.length,
                        },
                    });

                    let success;
                    let failuresData;
                    let failure;

                    if (response.isSuccess) {
                        failuresData = lodash.get(response, 'data.failures', []);
                        const successes = lodash.get(response, 'data.successes', null);
                        success = successes !== null && successes !== undefined && !Number.isNaN(successes)
                            ? Number(successes) : batch.length - failuresData.length;
                        failure = batch.length - success;
                    } else {
                        failuresData = lodash.get(response, 'data.failures', batch.map((_data, dataIdx) => ({
                            ..._data,
                            rowNum: (index * batchSize) + dataIdx + 1,
                            error_message: response.errorMessage || 'Something went wrong',
                            error_reason: response.errorMessage || 'Something went wrong',
                        })));
                        success = 0;
                        failure = batch.length;
                    }

                    setSuccessCount((prev) => {
                        const newCount = prev + success;
                        return newCount;
                    });

                    setFailures((prev) => {
                        const newFailures = [...prev, ...failuresData];
                        return newFailures;
                    });

                    setFailureCount((prev) => {
                        const newCount = prev + failure;
                        return newCount;
                    });

                    setPendingCount((prev) => {
                        const newCount = prev - batch.length;
                        return newCount;
                    });
                } catch (error) {
                    setFailureCount((prevCount) => prevCount + batch.length);
                    setPendingCount((prevCount) => prevCount - batch.length);
                }
            };
        });

        const processWithConcurrency = async () => {
            const chunks = lodash.chunk(promisesFns, concurrencyLimit);
            await Promise.all(chunks.map(async (chunk) => {
                await Promise.all(chunk.map((fn) => fn()));
            }));
        };

        try {
            await processWithConcurrency();
            if (onAllBatchesComplete) {
                await onAllBatchesComplete();
            }
            message.success(t(`The Operation Is Complete. You May Download The Failed Results If Any
                 By Clicking The Download Icon. To See Your Changes, Refresh The Data.`));
        } catch (error) {
            console.error('Error processing batches:', error);
            message.error(t('An error occurred while processing the batches.'));
        } finally {
            setIsProcessing(false);
            setUploadStatus(false);
        }
    };

    React.useEffect(() => {
        if (isVisible && !isProcessing) {
            // Check if another upload is in progress
            if (checkUploadInProgress()) {
                message.error(`An upload is already in progress,
                    please wait for it to complete and close the dialog before starting a new one`);
                onClose();
                return;
            }

            setUploadStatus(true);
            processBatches();
        }

        // eslint-disable-next-line consistent-return
        return () => {
            if (isVisible) {
                setUploadStatus(false);
            }
            return undefined;
        };
    }, [isVisible]);

    const downloadFailures = () => {
        if (!jobComplete) return;
        createAndDownloadCSVFileFromJSON(failures, `failure-data-${new Date().toISOString()}`, failureArrayKeysToShow);
    };

    const renderMetric = (status: string, icon: React.ReactNode, count: number) => (
        <div className={classes.metricTile}>
            <div className={classes.metricNumber}>{count}</div>
            <div className={classes.metricStatus}>
                {icon}
                <span>{t(status)}</span>
            </div>
        </div>
    );

    const handleClose = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (jobComplete) {
            setUploadStatus(false);
            onClose();
        } else {
            Modal.confirm({
                title: t('Attention'),
                content: t('If you move away, the process will be incomplete. Are you sure you want to proceed?'),
                okText: t('Proceed'),
                cancelText: t('Don\'t Proceed'),
                width: 450,
                onOk: () => {
                    setUploadStatus(false);
                    onClose();
                },
                okType: 'danger',
            });
        }
    };

    if (!isVisible) return null;

    const renderHeader = () => (
        <div className={classes.header} style={{ marginBottom: 0 }}>
            <Progress type="circle" percent={progressPercentage} width={40} />
            <span style={{ marginLeft: '16px', fontSize: '18px', fontWeight: 600 }}>{t(heading)}</span>
        </div>
    );

    const renderContent = () => (
        <>
            <div className={classes.progressData}>
                <span>
                    {progressPercentage}
                    %
                    {t('Completed')}
                </span>

                <Tooltip title={t('Download failed results')}>
                    <CloudDownloadOutlined
                        onClick={downloadFailures}
                        style={{
                            fontSize: '20px',
                            color: jobComplete ? '#333333' : '#CCCCCC',
                            cursor: jobComplete ? 'pointer' : 'not-allowed',
                        }}
                    />
                </Tooltip>
            </div>

            <div className={classes.progressMetrics}>
                {renderMetric('Pending', <ExclamationCircleFilled style={{ color: '#F0A105' }} />, pendingCount)}
                {renderMetric('Successful', <CheckCircleFilled style={{ color: '#20B249' }} />, successCount)}
                {renderMetric('Failed', <CloseCircleFilled style={{ color: '#D40B00' }} />, failureCount)}
            </div>
        </>
    );

    return (
        <div className={classes.floatingContainer}>
            <div className={classes.collapseWrapper}>
                <Collapse
                    defaultActiveKey={['1']}
                    expandIconPosition="right"
                    className={classes.collapsible}
                >
                    <Collapse.Panel
                        className={classes.panel}
                        header={renderHeader()}
                        key="1"
                        extra={
                            (
                                <CloseOutlined
                                    className={classes.closeIcon}
                                    onClick={(e) => handleClose(e)}
                                />
                            )
                        }
                    >
                        {renderContent()}
                    </Collapse.Panel>
                </Collapse>
            </div>
        </div>
    );
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(AsyncBatchModal);
